import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator } from '@fortawesome/free-solid-svg-icons';
import { DropDownList, MultiSelect } from '@progress/kendo-react-dropdowns';
import type { FieldTypeDefinition, FieldRendererProps, FieldEditorProps } from '../registry';
import type { CalcAggregation } from '../../types';
import { evaluateCalc } from '../../utils/calc';

const AGGREGATIONS: CalcAggregation[] = ['sum', 'average', 'count'];

function CalculatedRenderer({ field, answers }: FieldRendererProps) {
  const result = field.calc ? evaluateCalc(field.calc, answers) : 0;
  return (
    <div
      style={{
        padding: 'var(--kendo-spacing-2) var(--kendo-spacing-3)',
        borderRadius: 'var(--kendo-border-radius-md)',
        background: 'var(--kendo-color-base-subtle)',
        border: '1px solid var(--kendo-color-border)',
        fontWeight: 'var(--kendo-font-weight-bold)',
        fontSize: 'var(--kendo-font-size-xl)',
      }}
    >
      {result}
    </div>
  );
}

function CalculatedPropertyEditor({ field, onChange, allFields }: FieldEditorProps) {
  const calc = field.calc ?? { aggregation: 'sum' as CalcAggregation, sourceHandles: [] };
  const candidates = allFields.filter((f) => f.id !== field.id && f.handle);
  const selected = candidates.filter((f) => calc.sourceHandles.includes(f.handle));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--kendo-spacing-3)' }}>
      <div>
        <label
          style={{
            fontSize: 'var(--kendo-font-size-xs)',
            fontWeight: 'var(--kendo-font-weight-bold)',
            color: 'var(--kendo-color-subtle)',
            display: 'block',
            marginBottom: 'var(--kendo-spacing-1)',
          }}
        >
          Aggregation
        </label>
        <DropDownList
          data={AGGREGATIONS}
          value={calc.aggregation}
          onChange={(e) => onChange({ calc: { ...calc, aggregation: e.value } })}
        />
      </div>
      <div>
        <label
          style={{
            fontSize: 'var(--kendo-font-size-xs)',
            fontWeight: 'var(--kendo-font-weight-bold)',
            color: 'var(--kendo-color-subtle)',
            display: 'block',
            marginBottom: 'var(--kendo-spacing-1)',
          }}
        >
          Source fields
        </label>
        <MultiSelect
          data={candidates}
          textField="label"
          dataItemKey="id"
          value={selected}
          onChange={(e) =>
            onChange({
              calc: { ...calc, sourceHandles: (e.value ?? []).map((f: { handle: string }) => f.handle) },
            })
          }
        />
      </div>
    </div>
  );
}

export const definition: FieldTypeDefinition = {
  key: 'CALC',
  label: 'Calculated Field',
  category: 'advanced',
  icon: <FontAwesomeIcon icon={faCalculator} fixedWidth />,
  defaultProps: { label: 'Total score', isRequired: false, calc: { aggregation: 'sum', sourceHandles: [] } },
  Renderer: CalculatedRenderer,
  PropertyEditor: CalculatedPropertyEditor,
};
